import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, Loader, PageHeader, ScriptureRef } from "@/components/cast/Common";
import { Sparkles, ImageIcon } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { VerseCardDialog } from "@/components/cast/VerseCard";
import { PrayerWall } from "@/components/cast/PrayerWall";
import { Emblem } from "@/components/cast/Emblem";
import { TestimonyCard } from "@/components/cast/TestimonyCard";
import { StylePicker } from "@/components/cast/TestimonyStyle";
import { AcceptRules, RulesList } from "./Rules";

function FocusedPost({ id, onClose }: { id: string; onClose: () => void }) {
  const [p, setP] = useState<any>(undefined);
  useEffect(() => { setP(undefined); api.get(`/testimonies/${id}`).then((r) => setP(r.data)).catch(() => setP(null)); }, [id]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [id]);
  if (p === undefined) return <Loader />;
  return (
    <div className="rounded-3xl ring-2 ring-[#C86D51]/40 ring-offset-4 ring-offset-[#FAF7F2]" data-testid="focused-post">
      <div className="flex items-center justify-between px-2 pb-2"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C86D51]">From your notifications</p>
        <button onClick={onClose} data-testid="focused-post-close" className="text-sm font-semibold text-slate-500 hover:text-slate-800">Close</button></div>
      {p ? <TestimonyCard key={p.id} post={p} openComments onRemoved={onClose} /> : <p className="card-warm p-6 text-slate-500">This post is no longer available.</p>}
    </div>
  );
}

function DailyAffirmation() {
  const [a, setA] = useState<any>(null);
  const [card, setCard] = useState(false);
  useEffect(() => { api.get("/affirmation/today").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return null;
  return (
    <div className="relative overflow-hidden rounded-3xl bg-[#1E293B] p-6 text-amber-50 shadow-xl sm:p-8" data-testid="daily-affirmation">
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-amber-300/20 blur-2xl" />
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-amber-300"><Sparkles className="h-4 w-4" />Today's affirmation</p>
      <p className="mt-3 font-scripture text-2xl italic leading-relaxed sm:text-3xl" data-testid="daily-affirmation-text">{a.text}</p>
      <div className="mt-4 flex flex-wrap items-center gap-3"><ScriptureRef refText={a.ref} />
        <button onClick={() => setCard(true)} data-testid="affirmation-make-card" className="inline-flex items-center gap-2 rounded-full bg-amber-300 px-4 py-2 text-sm font-semibold text-amber-950 transition-transform hover:scale-105"><ImageIcon className="h-4 w-4" />Make a card</button>
      </div>
      {card && <VerseCardDialog open={card} onOpenChange={setCard} initialStyle="affirm" styles={["affirm"]} verse={{ ref: a.ref, text: a.text, version: "", kind: "Affirmation", eyebrow: "TODAY'S AFFIRMATION", footer: a.ref }} />}
    </div>
  );
}

function Composer({ onPosted }: { onPosted: (p: any) => void }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [ref, setRef] = useState("");
  const [style, setStyle] = useState("plain");
  const [kind, setKind] = useState("testimony");
  const [busy, setBusy] = useState(false);
  const aff = kind === "affirmation";
  const post = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/testimonies", { text, scripture_ref: ref, style, kind });
      setText(""); setRef(""); setStyle("plain"); onPosted(data);
      toast[data.status === "pending" ? "info" : "success"](data.status === "pending" ? "Thanks for sharing. A moderator will review it before it appears." : aff ? "Your affirmation is shared." : "Your testimony is shared.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-warm p-6" data-testid="testimony-composer">
      <div className="flex gap-4">
        <Emblem avatar={user.avatar} size={42} />
        <div className="flex-1 space-y-3">
          <div className="flex gap-2" data-testid="post-kind-picker">
            {[["testimony", "Testimony"], ["affirmation", "Affirmation"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setKind(k)} aria-pressed={kind === k} data-testid={`post-kind-${k}`}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${kind === k ? "bg-[#1E293B] text-white" : "bg-[#F3ECE0] text-slate-700 hover:bg-[#E9DFCF]"}`}>{l}</button>
            ))}
          </div>
          <Textarea data-testid="testimony-text-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={3000} placeholder={aff ? "Speak a truth from God's Word, e.g. I am loved and never alone. (text only)" : "What has God done in your life? (text only)"} className="min-h-[120px] bg-white font-scripture text-lg" />
          <StylePicker value={style} onChange={setStyle} />
          <div className="flex flex-wrap items-center gap-3">
            <Input data-testid="testimony-scripture-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Scripture tag (optional), e.g. Psalm 40:2" className="max-w-xs bg-white" />
            <span className="text-xs text-slate-500">{text.length}/3000 · posting as <b>{user.username}</b></span>
            <span className="flex-1" />
            <Button data-testid="testimony-post-submit-button" disabled={busy || text.trim().length < (aff ? 8 : 20)} onClick={post} className="rounded-full bg-[#C86D51] px-6 hover:bg-[#B35A3F]">{busy ? "Screening…" : aff ? "Share affirmation" : "Share testimony"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CastNet() {
  const { user } = useAuth();
  const [tab, setTab] = useState("all");
  const [kind, setKind] = useState("all");
  const [posts, setPosts] = useState<any[] | null>(null);
  useEffect(() => { if (user.rules_accepted_at) { setPosts(null); api.get(`/testimonies?tab=${tab}&kind=${kind}`).then((r) => setPosts(r.data)); } }, [tab, kind, user.rules_accepted_at]);
  const [params, setParams] = useSearchParams();
  const focus = params.get("post");
  const [section, setSection] = useState(params.get("section") === "prayer" ? "prayer" : "feed");

  if (!user.rules_accepted_at) {
    return (
      <div className="mx-auto max-w-3xl">
        <PageHeader metaphor="Cast Net" label="Testimonies & Affirmations" title="Before you join the conversation." subtitle="Please read our community rules. They keep this a safe place to share." />
        <div className="card-warm p-8" data-testid="rules-gate"><RulesList /><div className="mt-8"><AcceptRules /></div></div>
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader metaphor="Cast Net" label="Testimonies & Affirmations" title="What God has done, and who He says we are." subtitle="A moderated, text-only feed of testimonies and affirmations. React with Amen, Praying, or Praise, and reply to encourage one another." />
      <div className="space-y-8">
        <div className="flex gap-2 rounded-full bg-[#F3ECE0] p-1" data-testid="castnet-section-toggle">
          {[["feed", "Testimonies & Affirmations"], ["prayer", "Prayer requests"]].map(([k, l]) => (
            <button key={k} onClick={() => setSection(k)} aria-pressed={section === k} data-testid={`castnet-section-${k}`}
              className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${section === k ? (k === "prayer" ? "bg-[#E11D48] text-white shadow" : "bg-[#1E293B] text-white shadow") : "text-slate-600 hover:bg-white/60"}`}>{l}</button>
          ))}
        </div>
        {section === "prayer" ? <PrayerWall /> : <>
        {focus && <FocusedPost id={focus} onClose={() => setParams({}, { replace: true })} />}
        <DailyAffirmation />
        <Composer onPosted={(p) => { if (kind === "all" || (p.kind || "testimony") === kind) setPosts((prev) => [p, ...(prev || [])]); }} />
        <Tabs value={tab} onValueChange={setTab}><TabsList className="bg-[#F3ECE0]"><TabsTrigger value="all" data-testid="feed-tab-all">Everyone</TabsTrigger><TabsTrigger value="following" data-testid="feed-tab-following">Following</TabsTrigger></TabsList></Tabs>
        <div className="-mt-4 flex flex-wrap gap-2" data-testid="feed-kind-filter">
          {[["all", "All posts"], ["testimony", "Testimonies"], ["affirmation", "Affirmations"]].map(([k, l]) => (
            <button key={k} onClick={() => setKind(k)} aria-pressed={kind === k} data-testid={`feed-kind-${k}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${kind === k ? "border-[#C86D51] bg-[#FBF3EC] text-[#B35A3F]" : "border-[#E2DBCE] bg-white text-slate-600 hover:bg-[#FAF7F2]"}`}>{l}</button>
          ))}
        </div>
        {posts === null ? <Loader /> : posts.length === 0 ? <Empty testId="feed-empty" title={tab === "following" ? "Nothing yet from people you follow" : kind === "affirmation" ? "No affirmations yet" : "No testimonies yet"} body="Be the first to share." /> : (
          <div className="space-y-6" data-testid="testimony-feed">{posts.map((p) => <TestimonyCard key={p.id} post={p} onRemoved={(id, authorId) => setPosts((ps) => ps!.filter((x) => x.id !== id && (!authorId || x.author.id !== authorId)))} />)}</div>
        )}
        </>}
      </div>
    </div>
  );
}
